import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  ensureFirebase,
  explicitAuthDevMode,
  firebaseReloadAndRefreshToken,
  firebaseSendPasswordResetEmail,
  firebaseSignIn,
  firebaseSignOut,
  firebaseSignUp,
  getIdToken,
  makeDevToken,
  subscribeAuth,
} from "../lib/firebase";
import { friendlyAuthError } from "../lib/authErrors";
import { instructorApi, setAuthTokenGetter } from "../services/instructorApi";
import { partnerApi, setPartnerAuthTokenGetter } from "../services/partnerApi";

const AuthContext = createContext(null);

const DEV_STORAGE_KEY = "yoga_hanu_dev_auth";
const INTENT_STORAGE_KEY = "yoga_hanu_signup_intent";

function loadDevUser() {
  try {
    const raw = localStorage.getItem(DEV_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveDevUser(user) {
  if (!user) localStorage.removeItem(DEV_STORAGE_KEY);
  else localStorage.setItem(DEV_STORAGE_KEY, JSON.stringify(user));
}

export function getStoredSignupIntent() {
  try {
    return localStorage.getItem(INTENT_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function setStoredSignupIntent(intent) {
  try {
    if (intent === "partner" || intent === "instructor") {
      localStorage.setItem(INTENT_STORAGE_KEY, intent);
    } else {
      localStorage.removeItem(INTENT_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function clearStoredSignupIntent() {
  try {
    localStorage.removeItem(INTENT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Keep a plain snapshot so React state updates after reload(). */
function authUserSnapshot(user) {
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    emailVerified: user.emailVerified,
  };
}

export function AuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [devUser, setDevUser] = useState(null);
  const [isDevMode, setIsDevMode] = useState(explicitAuthDevMode);
  const [appUser, setAppUser] = useState(null);
  const [instructor, setInstructor] = useState(null);
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const getToken = useCallback(async () => {
    const { isAuthDevMode } = await ensureFirebase();
    if (isAuthDevMode && (devUser || loadDevUser())) {
      const u = devUser || loadDevUser();
      return makeDevToken({
        uid: u.uid,
        email: u.email,
        name: u.displayName,
        email_verified: u.emailVerified !== false,
      });
    }
    return getIdToken();
  }, [devUser]);

  useEffect(() => {
    setAuthTokenGetter(getToken);
    setPartnerAuthTokenGetter(getToken);
  }, [getToken]);

  const syncAccount = useCallback(async (displayName, options = {}) => {
    setSyncing(true);
    try {
      const intent =
        options.intent ||
        getStoredSignupIntent() ||
        (options.as === "partner" ? "partner" : null);

      const body = {};
      if (displayName) body.displayName = displayName;

      if (intent === "partner") {
        body.intent = "partner";
        // Do NOT force createInstructorProfile for partners
        body.createInstructorProfile = false;
      } else if (displayName) {
        body.createInstructorProfile = true;
      }

      // #region agent log
      fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'pre-fix',hypothesisId:'D',location:'AuthContext.jsx:syncAccount',message:'syncAccount starting',data:{intent,hasDisplayName:Boolean(displayName),bodyIntent:body.intent||null,createInstructorProfile:body.createInstructorProfile},timestamp:Date.now()})}).catch(()=>{});
      // #endregion

      const data = await instructorApi.sync(body);
      setAppUser(data.user);
      setInstructor(data.instructor);
      setPartner(data.partner || null);

      // #region agent log
      fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'pre-fix',hypothesisId:'E',location:'AuthContext.jsx:syncAccount-ok',message:'syncAccount succeeded',data:{role:data?.user?.role||null,hasPartner:Boolean(data?.partner),hasInstructor:Boolean(data?.instructor)},timestamp:Date.now()})}).catch(()=>{});
      // #endregion

      if (data.user?.role === "partner") {
        clearStoredSignupIntent();
      }

      return data;
    } catch (err) {
      // #region agent log
      fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'pre-fix',hypothesisId:'D',location:'AuthContext.jsx:syncAccount-error',message:'syncAccount failed',data:{error:String(err?.message||err)},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      throw err;
    } finally {
      setSyncing(false);
    }
  }, []);

  const refreshInstructor = useCallback(async () => {
    const data = await instructorApi.getInstructor();
    setInstructor(data);
    if (data?.user) setAppUser(data.user);
    return data;
  }, []);

  const refreshPartner = useCallback(async () => {
    const data = await partnerApi.getPartner();
    setPartner(data);
    if (data?.user) setAppUser(data.user);
    return data;
  }, []);

  useEffect(() => {
    let unsub = () => {};
    let cancelled = false;

    async function boot() {
      const { isAuthDevMode } = await ensureFirebase();
      if (cancelled) return;
      setIsDevMode(isAuthDevMode);

      if (isAuthDevMode) {
        const u = loadDevUser();
        setDevUser(u);
        if (u) {
          try {
            await syncAccount(u.displayName);
          } catch (err) {
            console.warn("Dev auth sync failed:", err.message);
          }
        }
        if (!cancelled) setLoading(false);
        return;
      }

      unsub = subscribeAuth(async (user) => {
        if (cancelled) return;
        setFirebaseUser(authUserSnapshot(user));
        if (user) {
          try {
            await syncAccount(user.displayName);
          } catch (err) {
            console.warn("Auth sync failed:", err.message);
          }
        } else {
          setAppUser(null);
          setInstructor(null);
          setPartner(null);
        }
        setLoading(false);
      });
    }

    boot();
    return () => {
      cancelled = true;
      unsub();
    };
  }, [syncAccount]);

  const signUp = useCallback(
    async (email, password, displayName, options = {}) => {
      if (options.intent === "partner" || options.as === "partner") {
        setStoredSignupIntent("partner");
      }
      const { isAuthDevMode } = await ensureFirebase();
      if (isAuthDevMode) {
        const u = {
          uid: `dev_${btoa(email).replace(/=+/g, "").slice(0, 16)}`,
          email,
          displayName,
          emailVerified: true,
        };
        saveDevUser(u);
        setDevUser(u);
        setIsDevMode(true);
        return syncAccount(displayName, options);
      }
      await firebaseSignUp(email, password, displayName);
      const data = await syncAccount(displayName, options);
      try {
        await instructorApi.sendVerificationEmail();
      } catch (err) {
        console.warn("Verification email send failed:", err.message);
      }
      return data;
    },
    [syncAccount]
  );

  const signIn = useCallback(
    async (email, password, options = {}) => {
      if (options.intent === "partner" || options.as === "partner") {
        setStoredSignupIntent("partner");
      }
      const { isAuthDevMode } = await ensureFirebase();
      if (isAuthDevMode) {
        const existing = loadDevUser();
        const u =
          existing?.email === email
            ? existing
            : {
                uid: `dev_${btoa(email).replace(/=+/g, "").slice(0, 16)}`,
                email,
                displayName: email.split("@")[0],
                emailVerified: true,
              };
        saveDevUser(u);
        setDevUser(u);
        setIsDevMode(true);
        return syncAccount(u.displayName, options);
      }
      await firebaseSignIn(email, password);
      return syncAccount(undefined, options);
    },
    [syncAccount]
  );

  const signOutUser = useCallback(async () => {
    const { isAuthDevMode } = await ensureFirebase();
    if (isAuthDevMode) {
      saveDevUser(null);
      setDevUser(null);
    } else {
      await firebaseSignOut();
    }
    setAppUser(null);
    setInstructor(null);
    setPartner(null);
    setFirebaseUser(null);
    clearStoredSignupIntent();
  }, []);

  const emailVerified = isDevMode
    ? true
    : Boolean(firebaseUser?.emailVerified || appUser?.email_verified);

  const resendVerificationEmail = useCallback(async () => {
    const { isAuthDevMode } = await ensureFirebase();
    if (isAuthDevMode) return;
    await instructorApi.sendVerificationEmail();
  }, []);

  const resetPassword = useCallback(async (email) => {
    const { isAuthDevMode } = await ensureFirebase();

    if (isAuthDevMode && explicitAuthDevMode) {
      return { skipped: true, reason: "dev" };
    }

    if (isAuthDevMode) {
      throw new Error(
        "Password reset is not configured yet. Please email info@yogaretreatsbooking.com for help."
      );
    }

    try {
      await firebaseSendPasswordResetEmail(email);
    } catch (err) {
      const code = err?.code || "";
      if (code === "auth/user-not-found" || code === "auth/invalid-email") {
        if (code === "auth/invalid-email") {
          throw new Error("Please enter a valid email address.");
        }
        return { skipped: false, reason: "sent" };
      }
      if (code === "auth/too-many-requests") {
        throw new Error("Too many reset attempts. Please try again later.");
      }
      if (code === "auth/configuration-not-found" || code === "auth/operation-not-allowed") {
        throw new Error(
          "Password reset is not configured yet. Please email info@yogaretreatsbooking.com for help."
        );
      }
      throw new Error(
        friendlyAuthError(err, "Unable to send reset email. Please try again.")
      );
    }
    return { skipped: false, reason: "sent" };
  }, []);

  const refreshEmailVerification = useCallback(async () => {
    const { isAuthDevMode } = await ensureFirebase();
    if (isAuthDevMode) return true;
    const user = await firebaseReloadAndRefreshToken();
    setFirebaseUser(authUserSnapshot(user));
    await syncAccount(user.displayName || undefined);
    return Boolean(user.emailVerified);
  }, [syncAccount]);

  const isPartner = appUser?.role === "partner" || Boolean(partner);
  const isInstructor = appUser?.role === "instructor" || Boolean(instructor);

  const value = useMemo(
    () => ({
      loading,
      syncing,
      isDevMode,
      firebaseUser,
      devUser,
      user: appUser,
      instructor,
      partner,
      emailVerified,
      isAuthenticated: Boolean(appUser || firebaseUser || devUser),
      isAdmin: appUser?.role === "admin",
      isInstructor,
      isPartner,
      signUp,
      signIn,
      signOut: signOutUser,
      resetPassword,
      syncAccount,
      refreshInstructor,
      refreshPartner,
      resendVerificationEmail,
      refreshEmailVerification,
      getToken,
    }),
    [
      loading,
      syncing,
      isDevMode,
      firebaseUser,
      devUser,
      appUser,
      instructor,
      partner,
      emailVerified,
      isInstructor,
      isPartner,
      signUp,
      signIn,
      signOutUser,
      resetPassword,
      syncAccount,
      refreshInstructor,
      refreshPartner,
      resendVerificationEmail,
      refreshEmailVerification,
      getToken,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
